import { useMemo, useState, type FormEvent } from 'react';
import { format } from 'date-fns';
import { ArrowRight, Check, Loader2, type LucideIcon } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Button,
  Input,
  Popover,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
} from '@clarix/ui';
import {
  DatePicker,
  DurationChips,
  PopoverContentWithArrow,
  TimePickerCell,
  UnifiedPeoplePicker,
  type PeoplePickerPerson,
} from '@clarix/ui-shared';
import { cn } from '@clarix/ui/lib/cn';
import { CALENDAR_PATH, CALENDAR_SIGNAL } from './constants';
import { QUICK_CREATE_COPY } from './copy';
import {
  buildEventBody,
  defaultCalendarId,
  formatClockAt,
  minStartToday,
  nextSlot,
  personAsUser,
  QUICK_CREATE_MINUTES,
  quickCreateApi,
  quickCreateError,
  quickCreateKeys,
  slotWindow,
  type QuickCreateMode,
  type QuickField,
} from './quickCreate';

/** The shared TimePickerCell's trigger is a table cell; restyled here to sit in a form row. */
const TIME_SLOT =
  'inline-flex [&>button]:mx-0 [&>button]:h-8 [&>button]:w-auto [&>button]:rounded-md [&>button]:border [&>button]:border-border [&>button]:px-2.5 [&>button]:text-lg [&>button]:text-foreground [&>button:hover]:bg-muted/60';

interface QuickCreatePopoverProps {
  mode: QuickCreateMode;
  icon: LucideIcon;
  slug: string;
  selfId: string | null;
  h24: boolean;
  openCalendarPath: (path: string) => void;
}

/** One click in the top bar opens a few fields; Save books it without leaving the page. */
export function QuickCreatePopover({ mode, icon: Icon, slug, selfId, h24, openCalendarPath }: QuickCreatePopoverProps) {
  const [open, setOpen] = useState(false);
  const label = QUICK_CREATE_COPY[mode].title;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={label}
              className={cn(
                'text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring grid h-6 w-6 shrink-0 place-items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2',
                open && 'bg-muted text-foreground',
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        {!open && <TooltipContent side="bottom">{label}</TooltipContent>}
      </Tooltip>
      {/* The shared arrow sits 20px in from the left edge; -8 puts it under this 24px button's centre. */}
      <PopoverContentWithArrow
        side="bottom"
        align="start"
        alignOffset={-8}
        collisionPadding={8}
        className="w-[340px] p-3"
      >
        {/* Mounted per open, so every open starts from the next free half hour. */}
        {open && (
          <QuickCreateForm
            mode={mode}
            slug={slug}
            selfId={selfId}
            h24={h24}
            onDone={() => setOpen(false)}
            openCalendarPath={(path) => {
              setOpen(false);
              openCalendarPath(path);
            }}
          />
        )}
      </PopoverContentWithArrow>
    </Popover>
  );
}

interface QuickCreateFormProps {
  mode: QuickCreateMode;
  slug: string;
  selfId: string | null;
  h24: boolean;
  onDone: () => void;
  openCalendarPath: (path: string) => void;
}

function QuickCreateForm({ mode, slug, selfId, h24, onDone, openCalendarPath }: QuickCreateFormProps) {
  const copy = QUICK_CREATE_COPY[mode];
  const [initial] = useState(() => nextSlot(new Date()));
  const [title, setTitle] = useState('');
  const [day, setDay] = useState(initial.day);
  const [time, setTime] = useState(initial.time);
  const [minutes, setMinutes] = useState(QUICK_CREATE_MINUTES[mode]);
  const [people, setPeople] = useState<PeoplePickerPerson[]>([]);
  const [roomId, setRoomId] = useState<string | null>(null);
  // Required-field messages appear once Save is pressed, then follow the fields as they change.
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<{ field: QuickField | null; message: string } | null>(null);

  const { start, end } = slotWindow(day, time, minutes);
  const startIso = start.toISOString();
  const endIso = end.toISOString();

  const calendars = useQuery({
    queryKey: quickCreateKeys.calendars(slug),
    queryFn: () => quickCreateApi.calendars(slug),
    staleTime: 5 * 60_000,
  });
  const peopleQ = useQuery({
    queryKey: quickCreateKeys.people(slug),
    queryFn: () => quickCreateApi.people(slug),
    staleTime: 5 * 60_000,
  });
  const roomsQ = useQuery({
    queryKey: quickCreateKeys.rooms(slug, startIso, endIso),
    queryFn: () => quickCreateApi.rooms(slug, startIso, endIso),
    enabled: mode === 'room',
    staleTime: 15_000,
  });
  const invitees = useMemo(
    () => (peopleQ.data ?? []).filter((p) => String(p.id) !== selfId).map(personAsUser),
    [peopleQ.data, selfId],
  );
  const freeRooms = useMemo(() => (roomsQ.data ?? []).filter((r) => r.free), [roomsQ.data]);
  // A room picked for an earlier time may be busy at the new one.
  const pickedRoom = roomId && freeRooms.some((r) => r.room.id === roomId) ? roomId : null;

  const calendarId = defaultCalendarId(calendars.data);
  const name = mode === 'room' ? QUICK_CREATE_COPY.room.eventTitle : title.trim();
  const required: Partial<Record<QuickField, string>> = {
    ...(!name ? { name: QUICK_CREATE_COPY.required.name } : {}),
    ...(mode === 'room' && !pickedRoom ? { room: QUICK_CREATE_COPY.required.room } : {}),
  };
  const fieldError = (field: QuickField): string | null =>
    (submitted ? required[field] : undefined) ?? (serverError?.field === field ? serverError.message : null);
  const formError = serverError && !serverError.field ? serverError.message : null;

  const create = useMutation({
    mutationFn: () =>
      quickCreateApi.create(
        slug,
        buildEventBody({ title: name, calendarId: calendarId ?? '', day, time, minutes, people, roomId: pickedRoom }),
      ),
    onSuccess: (event) => {
      window.dispatchEvent(new Event(CALENDAR_SIGNAL.EVENTS_CHANGED));
      toast.success({
        title: copy.done,
        description: `${name} · ${format(start, 'EEE, MMM d')} ${formatClockAt(start, h24)}`,
        action: { label: QUICK_CREATE_COPY.open, onClick: () => openCalendarPath(CALENDAR_PATH.event(event.id, event.start)) },
        duration: 6_000,
      });
      onDone();
    },
    onError: (err) => {
      const problem = quickCreateError(err);
      if (problem.field === 'room') {
        setRoomId(null);
        void roomsQ.refetch();
      }
      setServerError(problem);
    },
  });

  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    setSubmitted(true);
    setServerError(null);
    if (Object.keys(required).length > 0 || create.isPending) return;
    if (!calendarId) {
      setServerError({ field: null, message: QUICK_CREATE_COPY.noCalendar });
      return;
    }
    create.mutate();
  };
  // A field the person changes drops the server's complaint about it.
  const clearServer = (field: QuickField) => setServerError((cur) => (cur?.field === field ? null : cur));

  const moreOptions = () =>
    openCalendarPath(
      CALENDAR_PATH.newMeeting({ title: title.trim(), date: format(day, 'yyyy-MM-dd'), time, minutes }),
    );

  return (
    <form onSubmit={submit} className="flex flex-col gap-3" aria-label={copy.title}>
      <p className="text-foreground text-xl font-semibold">{copy.title}</p>

      {mode === 'meeting' && (
        <div className="flex flex-col gap-1">
          <Input
            autoFocus
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              clearServer('name');
            }}
            placeholder={QUICK_CREATE_COPY.namePlaceholder}
            aria-label={QUICK_CREATE_COPY.nameLabel}
            aria-required="true"
            aria-invalid={!!fieldError('name')}
            maxLength={255}
          />
          <FieldError message={fieldError('name')} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        <DatePicker
          value={day}
          onChange={(next) => {
            setDay(next);
            clearServer('time');
          }}
          minDate={initial.day}
          triggerClassName="h-8"
        />
        <span className={TIME_SLOT}>
          <TimePickerCell
            value={time}
            minTime={minStartToday(day, new Date())}
            ariaLabel={QUICK_CREATE_COPY.startLabel}
            display={<span className="whitespace-nowrap">{formatClockAt(start, h24)}</span>}
            onCommit={(next) => {
              if (!next) return;
              setTime(next);
              clearServer('time');
            }}
          />
        </span>
        <ArrowRight className="text-muted-foreground h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="text-muted-foreground whitespace-nowrap text-lg tabular-nums">{formatClockAt(end, h24)}</span>
      </div>
      <FieldError message={fieldError('time')} />

      <div role="group" aria-label={QUICK_CREATE_COPY.durationLabel}>
        <DurationChips selectedMinutes={minutes} onSelect={setMinutes} />
      </div>

      <div role="group" aria-label={QUICK_CREATE_COPY.peopleLabel} className="flex flex-col gap-1">
        <UnifiedPeoplePicker
          selected={people}
          onSelectedChange={(next) => {
            setPeople(next);
            clearServer('people');
          }}
          allUsers={invitees}
        />
        <FieldError message={fieldError('people')} />
      </div>

      {mode === 'room' && (
        <RoomChoices
          loading={roomsQ.isPending || (roomsQ.isFetching && !roomsQ.data)}
          failed={roomsQ.isError}
          rooms={freeRooms}
          picked={pickedRoom}
          onPick={(id) => {
            setRoomId(id);
            clearServer('room');
          }}
        />
      )}
      {mode === 'room' && <FieldError message={fieldError('room')} />}

      <FieldError message={formError} />

      <div className="flex items-center justify-between gap-2 pt-1">
        {mode === 'meeting' ? (
          <Button type="button" variant="ghost" size="sm" onClick={moreOptions}>
            {QUICK_CREATE_COPY.moreOptions}
          </Button>
        ) : (
          <span />
        )}
        <Button type="submit" size="sm" disabled={create.isPending || calendars.isPending}>
          {create.isPending && <Loader2 className="animate-spin" aria-hidden />}
          {copy.submit}
        </Button>
      </div>
    </form>
  );
}

function FieldError({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return (
    <p role="alert" className="text-destructive-fg text-md break-words">
      {message}
    </p>
  );
}

interface RoomChoicesProps {
  loading: boolean;
  failed: boolean;
  rooms: readonly { room: { id: string; name: string; floor: string | null; capacity: number } }[];
  picked: string | null;
  onPick: (id: string) => void;
}

/** Only rooms free for the whole slot; a pick is one click. */
function RoomChoices({ loading, failed, rooms, picked, onPick }: RoomChoicesProps) {
  if (loading) return <p className="text-muted-foreground text-md">{QUICK_CREATE_COPY.findingRooms}</p>;
  if (failed) return <p className="text-destructive-fg text-md">{QUICK_CREATE_COPY.roomsFailed}</p>;
  if (rooms.length === 0) return <p className="text-muted-foreground text-md">{QUICK_CREATE_COPY.noRooms}</p>;
  return (
    <div role="radiogroup" aria-label={QUICK_CREATE_COPY.roomsLabel} className="-mx-1 flex max-h-52 flex-col gap-0.5 overflow-y-auto px-1">
      {rooms.map(({ room }) => {
        const selected = room.id === picked;
        return (
          <button
            key={room.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onPick(room.id)}
            className={cn(
              'focus-visible:ring-ring flex min-w-0 items-center gap-2 rounded-md border px-2.5 py-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2',
              selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/60 border-transparent',
            )}
          >
            <span className="min-w-0 flex-1">
              <span className="text-foreground block truncate text-lg font-medium">{room.name}</span>
              <span className="text-muted-foreground block truncate text-md">
                {[room.floor, QUICK_CREATE_COPY.seats(room.capacity)].filter(Boolean).join(' · ')}
              </span>
            </span>
            {selected && <Check className="text-primary h-4 w-4 shrink-0" aria-hidden />}
          </button>
        );
      })}
    </div>
  );
}
